import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import EstadoVazio from '@/components/ui/EstadoVazio';
import ModalLancamento from '../direcao/components/caixa/ModalLancamento';
import type { AbaPainel } from './types';
import type { FinanceiroDoEvento, ExtratoLinha } from './financeiro/tipos';
import { horaBR, slugificar } from './financeiro/rotulos';
import BlocoResumo from './financeiro/BlocoResumo';
import BlocoDeOndeVeio from './financeiro/BlocoDeOndeVeio';
import BlocoOndeEsta from './financeiro/BlocoOndeEsta';
import BlocoEstornos from './financeiro/BlocoEstornos';
import BlocoCaixa from './financeiro/BlocoCaixa';
import BlocoExtrato from './financeiro/BlocoExtrato';
import TotaisPublicos from './financeiro/TotaisPublicos';

interface Props {
  eventoId: number;
  mostrarLoja: boolean;
  /** Entrada franca que vende fichas (Quermesse): o dinheiro vem dos pedidos de ficha. */
  entradaFranca?: boolean;
  /** Acampamento: mostra os totais separados de Equipe e Campistas. */
  separaEquipeCampista?: boolean;
  aoIrPara?: (aba: AbaPainel, extra?: Record<string, string>) => void;
}

const CATEGORIAS_VAZIAS: string[] = [];

export default function AbaFinanceiro({
  eventoId,
  entradaFranca = false,
  separaEquipeCampista = false,
}: Props) {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [restrito, setRestrito] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [dados, setDados] = useState<FinanceiroDoEvento | null>(null);
  const [extrato, setExtrato] = useState<ExtratoLinha[]>([]);
  const [catEntrada, setCatEntrada] = useState<string[]>(CATEGORIAS_VAZIAS);
  const [catDespesa, setCatDespesa] = useState<string[]>(CATEGORIAS_VAZIAS);
  const [modal, setModal] = useState<'entrada' | 'saida' | null>(null);

  const blocoDRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  async function carregar() {
    setErro(null);
    try {
      // Uma única chamada para todo o resumo do financeiro. A lista do extrato
      // (vw_extrato_evento) e as categorias dos lançamentos continuam separadas.
      const [resRpc, resExtrato, resCatEnt, resCatDes] = await Promise.all([
        supabase.rpc('financeiro_do_evento', { p_evento: eventoId }),
        supabase
          .from('vw_extrato_evento')
          .select('*')
          .eq('evento_id', eventoId)
          .order('quando', { ascending: false })
          .range(0, 499),
        supabase.from('categorias_entrada').select('nome, ordem').order('ordem'),
        supabase.from('categorias_despesa').select('nome, ordem').order('ordem'),
      ]);

      if (resRpc.error) {
        if (resRpc.error.code === '42501') {
          setRestrito(true);
          return;
        }
        throw resRpc.error;
      }

      setRestrito(false);
      setDados(resRpc.data as FinanceiroDoEvento);

      // A view do extrato não traz o comprovante; buscamos na tabela `despesas`
      // pelo id do lançamento. Se falhar, o extrato segue funcionando (o anexo
      // só não aparece).
      const linhas = (resExtrato.data as ExtratoLinha[]) ?? [];
      const idsDespesas = linhas
        .filter((l) => l.origem === 'despesa')
        .map((l) => Number(l.lancamento_id));
      const mapaComprovante = new Map<
        number,
        {
          comprovante_path: string | null;
          comprovante_nome: string | null;
          comprovante_tipo: string | null;
        }
      >();
      if (idsDespesas.length) {
        try {
          const { data: comps, error: errComp } = await supabase
            .from('despesas')
            .select('id, comprovante_path, comprovante_nome, comprovante_tipo')
            .in('id', idsDespesas);
          if (!errComp && comps) {
            for (const c of comps as {
              id: number | string;
              comprovante_path: string | null;
              comprovante_nome: string | null;
              comprovante_tipo: string | null;
            }[]) {
              mapaComprovante.set(Number(c.id), c);
            }
          }
        } catch {
          /* segue sem os comprovantes */
        }
      }

      setExtrato(
        linhas.map((l) => {
          const c = mapaComprovante.get(Number(l.lancamento_id));
          return {
            ...l,
            comprovante_path: c?.comprovante_path ?? null,
            comprovante_nome: c?.comprovante_nome ?? null,
            comprovante_tipo: c?.comprovante_tipo ?? null,
          };
        }),
      );
      if (resCatEnt.data) {
        setCatEntrada((resCatEnt.data as { nome: string }[]).map((c) => c.nome));
      }
      if (resCatDes.data) {
        setCatDespesa((resCatDes.data as { nome: string }[]).map((c) => c.nome));
      }
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível carregar o financeiro.'));
    } finally {
      setCarregando(false);
    }
  }

  // Carga inicial e ao trocar de evento.
  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  // Tempo real: uma única assinatura em `caixa_sinais`, filtrada pelo evento.
  // Ao sinalizar, relê a RPC com debounce de 400 ms. Também relê ao voltar a aba.
  useEffect(() => {
    const agendar = () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => void carregar(), 400);
    };
    const canal = supabase
      .channel(`financeiro-evento-${eventoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caixa_sinais',
          filter: `evento_id=eq.${eventoId}`,
        },
        () => agendar(),
      )
      .subscribe();

    const aoVoltar = () => {
      if (document.visibilityState === 'visible') agendar();
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      supabase.removeChannel(canal);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  if (restrito) {
    return (
      <EstadoVazio
        icone="ri-lock-line"
        titulo="Acesso restrito"
        descricao="Só quem está na direção deste evento pode ver o financeiro."
      />
    );
  }

  if (carregando && !dados) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[116px] esqueleto rounded-3xl bg-secondary-100" />
          ))}
        </div>
        <div className="h-44 esqueleto rounded-3xl bg-secondary-100" />
        <div className="h-56 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (!dados) {
    return (
      <Cartao className="p-10 text-center">
        <p className="text-[15px] text-foreground-800">
          {erro ?? 'Não foi possível carregar o financeiro.'}
        </p>
        <button
          type="button"
          onClick={() => {
            setCarregando(true);
            void carregar();
          }}
          className="mx-auto mt-4 flex cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </Cartao>
    );
  }

  const slug = slugificar(dados.evento?.nome ?? 'evento');

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <DicaPrimeiraVez
          id="painel.financeiro.primeira"
          texto={
            entradaFranca
              ? 'Tudo aqui vem dos pagamentos confirmados. O que a pessoa ainda não pagou não entra. Se o valor parecer estranho, confira em Compradores quem está esperando Pix.'
              : undefined
          }
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Botao
            variante="secundario"
            icone="ri-printer-line"
            onClick={() => navigate(`/gerenciar/${eventoId}/relatorio-fechamento`)}
          >
            Relatório do fechamento (PDF)
          </Botao>
          {dados.gerado_em && (
            <span className="flex items-center gap-1.5 whitespace-nowrap text-[12px] text-foreground-400">
              <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
              Atualizado às {horaBR(dados.gerado_em)}
            </span>
          )}
        </div>
      </div>

      {erro && (
        <div className="rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</div>
      )}

      {separaEquipeCampista && <TotaisPublicos eventoId={eventoId} />}

      <BlocoResumo
        resumo={dados.resumo}
        aoIrParaEstornos={() =>
          blocoDRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      />

      <BlocoDeOndeVeio origens={dados.por_origem ?? []} resumo={dados.resumo} />

      <BlocoOndeEsta
        evento={dados.evento}
        ondeEsta={dados.onde_esta}
        repasses={dados.repasses ?? []}
        conferencia={dados.conferencia}
      />

      <div ref={blocoDRef} className="scroll-mt-24">
        <BlocoEstornos estornos={dados.estornos ?? []} />
      </div>

      <BlocoCaixa
        caixa={dados.caixa}
        aoLancarEntrada={() => setModal('entrada')}
        aoLancarDespesa={() => setModal('saida')}
      />

      <BlocoExtrato
        linhas={extrato}
        slug={slug}
        eventoId={eventoId}
        aoMudar={() => void carregar()}
      />

      {modal && (
        <ModalLancamento
          tipo={modal}
          editar={null}
          categorias={modal === 'entrada' ? catEntrada : catDespesa}
          eventoId={eventoId}
          aoFechar={() => {
            setModal(null);
            void carregar();
          }}
        />
      )}
    </div>
  );
}